"use client";

import { useState, useEffect } from "react";
import type { MigrationResponse } from "../services/migration/types";
import { getMigrationById } from "../services/migration/migrationById";
import { extractApiErrorMessage } from "../utils/error-handling";

interface UseMigrationDetailsProps {
  migrationId: string;
  autoFetch?: boolean;
}

interface UseMigrationDetailsResult {
  migrationData: MigrationResponse | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook for fetching and managing migration details
 */
export function useMigrationDetails({
  migrationId,
  autoFetch = true,
}: UseMigrationDetailsProps): UseMigrationDetailsResult {
  const [migrationData, setMigrationData] = useState<MigrationResponse | null>(null);
  const [loading, setLoading] = useState(autoFetch);
  const [error, setError] = useState<string | null>(null);

  const fetchMigrationDetails = async () => {
    if (!migrationId) return;

    try {
      setLoading(true);
      setError(null);

      const response = await getMigrationById(migrationId);
      if (response.success && response.content) {
        // Transform the response to match the MigrationResponse type
        const transformedResponse: MigrationResponse = {
          ...response,
          error: response.error ? extractApiErrorMessage(response.error) : undefined,
        };
        setMigrationData(transformedResponse);
      } else {
        setError(extractApiErrorMessage(response.error) || "Error fetching migration details");
      }
    } catch (err) {
      console.error("Error in fetchMigrationDetails:", err);
      setError("Unexpected error loading migration details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (autoFetch) {
      fetchMigrationDetails();
    }
  }, [migrationId, autoFetch]);

  return {
    migrationData,
    loading,
    error,
    refetch: fetchMigrationDetails,
  };
}
